// MonthSelector.js
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toMonthInputValue, fromMonthInputValue } from '../utils';
import sprite from '../images/icons.svg';

const MonthSelector = ({ selectedDate, setSelectedDate, lang = 'sv-SE' }) => {
  const monthInputRef = useRef(null);
  const { t, i18n } = useTranslation();

  const currentLang = lang || i18n?.language || 'sv-SE';
  const currentSelected = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);

  const now = new Date();
  const isCurrentMonth =
    currentSelected.getFullYear() === now.getFullYear() &&
    currentSelected.getMonth() === now.getMonth();

  const options = { month: 'long', year: 'numeric' };
  const formattedDate = currentSelected.toLocaleDateString(currentLang, options);
  const formattedString = formattedDate.replace(' ', ' - ');

  const handlePreviousMonth = () => {
    const previousMonth = new Date(
      currentSelected.getFullYear(),
      currentSelected.getMonth() - 1,
      1
    );
    setSelectedDate(previousMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(
      currentSelected.getFullYear(),
      currentSelected.getMonth() + 1,
      1
    );
    setSelectedDate(nextMonth);
  };

  const handleMonthChange = (e) => {
    const value = e.target.value;
    if (!value) return;
    const newDate = fromMonthInputValue(value);
    if (newDate && !isNaN(newDate.getTime())) {
      setSelectedDate(newDate);
    }
  };

  const handleCurrentMonth = () => {
    setSelectedDate(new Date());
  };

  const handleOpenPicker = () => {
    if (monthInputRef.current) {
      if (typeof monthInputRef.current.showPicker === 'function') {
        try {
          monthInputRef.current.showPicker();
        } catch (err) {
          monthInputRef.current.focus();
        }
      } else {
        monthInputRef.current.focus();
      }
    }
  };

  return (
    <span className="select-date flex flex-align-center-h wrapper-width center">
      <button
        type="button"
        className="date-nav-arrow-btn"
        onClick={handlePreviousMonth}
        aria-label="Previous month"
      >
        <svg style={{ transform: 'rotate(180deg)', cursor: 'pointer' }} width="31" height="27">
          <use href={sprite + "#svg-arrow-right"} />
        </svg>
      </button>

      <div className="date-display-wrapper flex flex-align-center-h">
        <button
          type="button"
          className="date-picker-trigger-btn flex flex-align-center-h"
          onClick={handleOpenPicker}
          title={t('jump-to-date')}
          aria-label={t('jump-to-date')}
        >
          <span className="date-text bold">{formattedString}</span>
          <svg className="calendar-icon" width="20" height="20" aria-hidden="true">
            <use href={sprite + "#svg-calendar"} />
          </svg>
        </button>

        <input
          ref={monthInputRef}
          type="month"
          className="date-picker-input"
          value={toMonthInputValue(currentSelected)}
          onChange={handleMonthChange}
          aria-label={t('select-month')}
          tabIndex={-1}
        />
      </div>

      <button
        type="button"
        className="date-nav-arrow-btn"
        onClick={handleNextMonth}
        aria-label="Next month"
      >
        <svg style={{ cursor: 'pointer' }} width="31" height="27">
          <use href={sprite + "#svg-arrow-right"} />
        </svg>
      </button>

      {!isCurrentMonth && (
        <button
          type="button"
          className="today-button selectable-button bold"
          onClick={handleCurrentMonth}
          aria-label={t('current-month')}
        >
          {t('current-month')}
        </button>
      )}
    </span>
  );
};

export default MonthSelector;
export { MonthSelector };
